/**
 * Till Close Service
 *
 * Clean close-till flow extracted from cashToKeep.js.
 *
 * Flow:
 *   1. Print receipt IMMEDIATELY (don't wait for API)
 *   2. Build payload from unsynced events
 *   3. Call server API (best-effort, don't block logout)
 *   4. Logout (always, regardless of API result)
 *
 * If API fails, event is marked isSynced=0 for retry on next login (via syncOfflineDta).
 */
import db from "../database";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import { tillClosingPrint } from "../lib/printer/tillCloseReceipt";
import TillCloseHCPrint from "../lib/printer/tillCloseHCPrinter";
import TillCloseCWHCPrint from "../lib/printer/tillCloseCWHCprinter";
import { upsertTill, upsertPOSActivity, getTills } from "./tillServices";
import { trace } from "../utils/debugTrace";
import { getTillBusinessDate } from "../utils/tillBusinessDate";
import {
  closingTillOf,
  resolveLocalRelease,
  resolveServerRelease,
  readOwnDeviceId,
  toActivityRow,
  isActivityOk,
  logTillLifecycle,
} from "../utils/tillLogoutRelease";
import { unsentBillsKeepTill } from "../utils/tillDeviceRegistration";
import { parseTillAccess } from "../constants/accessControl";
import useAuthStore from "../stores/useAuthStore";
import useSessionStore from "../stores/useSessionStore";
import usePOSConfigStore from "../stores/usePOSConfigStore";
import env from "../config/env";

// ── Helpers ────────────────────────────────────────────────────────────────────

function generateId() {
  return uuidv4().replace(/-/g, "").toUpperCase();
}

function now() {
  return dayjs(new Date()).format("YYYY-MM-DD HH:mm:ss");
}

// Clears every browser cookie (accessToken, Enterprise.accessToken, username, etc).
// Must run on every real logout — if session cookies survive, the SSO bootstrap on
// /sign-in (Authentication/index.js `check()`) reads them back and silently
// re-authenticates the same user, which looks like "Close Store logs you back in".
function clearAllCookies(domainURL) {
  const isLocalhost = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
  const domainPart = isLocalhost ? "" : `domain=${domainURL};`;
  const securePart = isLocalhost ? "" : "secure=true;";
  const cookies = document.cookie.split(";");
  for (const cookie of cookies) {
    const eqPos = cookie.indexOf("=");
    const name = (eqPos > -1 ? cookie.substr(0, eqPos) : cookie).trim();
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;${securePart}SameSite=Strict;${domainPart}`;
  }
}

// ── Get unsynced events ────────────────────────────────────────────────────────

async function getUnsyncedEvents() {
  const allEvents = await db.tillEvents.toArray();
  const valid = [];

  for (const e of allEvents) {
    const isSyncNeeded = !e.isSynced || e.isSynced === 0 || e.isSynced === "0";
    if (!isSyncNeeded) continue;

    const hasData = e.allPaymentsData && e.allPaymentsData.length > 0;
    if (!hasData) {
      // Quarantine corrupted events instead of silently skipping
      try {
        if (window.electronAPI?.dataHealth) {
          // Report to main process for quarantine tracking
        }
      } catch {}
      continue;
    }
    valid.push(e);
  }

  return valid;
}

// ── Build tillCash payload ─────────────────────────────────────────────────────

function buildCashDenominationEvents(denominations, prc) {
  return (denominations || []).map((d) => ({
    cwrCashEventdetailsID: generateId(),
    count: d.count,
    amount: parseFloat(d.amount.toFixed(prc)),
    denomination: d.value,
  }));
}

function buildCashInOutEntries(cashInCashOutData) {
  let cashIn = 0,
    cashOut = 0,
    pettyCashIn = 0,
    pettyCashOut = 0;
  const entries = [];

  for (const item of cashInCashOutData) {
    const amount = parseFloat(item.amount);
    const time = dayjs(item.date, "DD-MM-YYYY HH:mm:ss").format("YYYY-MM-DD HH:mm:ss");
    const timestamp = now();
    const base = { cwrTillCashInOutId: item.key, amount, time, updated: timestamp, created: timestamp, description: item.note || "" };

    if (item.type === "cashIn") {
      cashIn += amount;
      entries.push({ ...base, type: "CI" });
    } else if (item.type === "cashOut") {
      cashOut += amount;
      entries.push({ ...base, type: "CO", paymentMode: "cash" });
    }
    if (item.type === "pettyCashIn") {
      pettyCashIn += amount;
      entries.push({ ...base, type: "PCI" });
    }
    if (item.type === "pettyCashOut") {
      pettyCashOut += amount;
      entries.push({ ...base, type: "PCO", paymentMode: "cash" });
    }
    if (item.type === "bankTransfer") {
      cashOut += amount;
      entries.push({ ...base, type: "CO", paymentMode: "cash" });
    }
  }

  return { entries, cashIn, cashOut, pettyCashIn, pettyCashOut };
}

function getMatchedTillSession(ordersDataArr, tillSessionId) {
  let matched = {};
  ordersDataArr.forEach((item) => {
    if (item.tillSessionId === tillSessionId) {
      matched = item;
    }
  });
  return matched;
}

async function buildTillCashForEvent(event, { tillData, noteValue, cashToKeep, checkedValue, posConfig }) {
  const prc = tillData.tillAccess.csBunit.currencies[0].prcPrecision;
  const tillCash = [];
  const tillRegistration = await db.tillRegistrations.toArray();
  // TIC-10115: this array feeds upsertPOSActivity(tillRegistration, "LO") further
  // up the close-till flow (see executeTillSync below), and that call reads only
  // tillRegistration[0] (see tillServices.js upsertPOSActivity: tillObj =
  // Array.isArray(tillData) ? tillData[0] : tillData). db.tillRegistrations is
  // append-only (Till.js .add() call sites), so with more than one row here,
  // [0] is whichever till was registered FIRST on this device, not necessarily
  // the till actually closing now — the "LO" release would target the wrong
  // till, leaving the real one's backend lock stuck (matches Keertana's
  // TILL_ACTIVE_CHECK RCA). Trace the full set so a recurrence shows whether
  // this is in play.
  trace("TillClose", "buildTillCashForEvent: tillRegistrations read for LO release", {
    count: tillRegistration.length,
    tills: tillRegistration.map((r) => r?.tillAccess?.[0]?.cwrTill?.till ?? r?.tillAccess?.cwrTill?.till),
    willReleaseIndex0Till: tillRegistration[0]?.tillAccess?.[0]?.cwrTill?.till ?? tillRegistration[0]?.tillAccess?.cwrTill?.till,
    eventTillSessionId: event?.tillSessionId,
  });
  const retainAmt = checkedValue === "0" && posConfig?.manualCashToKeep === "N" ? useSessionStore.getState().tillSession?.totalOpeningAmount : cashToKeep || 0;

  for (const paymentObj of event.allPaymentsData) {
    if (paymentObj.name.toLowerCase() === "cash") {
      // Opening/closing denomination events
      const openedCashEvents = buildCashDenominationEvents(event.openingCashDenominations, prc);
      const closedCashEvents = buildCashDenominationEvents(event.closingCashDenominations, prc);

      // Set notes on event
      db.tillEvents.where("tillSessionId").equals(event.tillSessionId).modify({ notes: noteValue });

      // Cash in/out entries
      const cashInCashOutData = await db.cashInCashOut.toArray();
      const cio = buildCashInOutEntries(cashInCashOutData);

      // Matched orders data
      const ordersDataArr = await db.ordersData.toArray();
      const matched = getMatchedTillSession(ordersDataArr, event.tillSessionId);

      // [DI-14] Guard against division by zero when grossTotal/grossReturnTotal is 0
      const netSalesAmount = paymentObj.cashSaleAmount > 0 && matched.grossTotal ? parseFloat(matched.netTotal * (paymentObj.cashSaleAmount / matched.grossTotal)) : 0;
      const returnTaxAmount =
        Math.abs(matched.totaReturnlTax) > 0 && Math.abs(paymentObj.paymentReturn) > 0 && Math.abs(matched.grossReturnTotal) > 0
          ? matched.netReturnTotal * (paymentObj.paymentReturn / matched.grossReturnTotal)
          : 0;

      const tillCashData = (event.serverTillCash || []).filter((ele) => ele.finPaymentmethodId === paymentObj.finPaymentmethodId);

      tillCash.push({
        cwrTillCashId: tillCashData.length > 0 ? tillCashData[0].cwrTillCashId : generateId(),
        // BUG-2171: the session's business date — a till opened last evening for today
        // must close under today, not yesterday's opening date.
        date: getTillBusinessDate(event) || dayjs().format("YYYY-MM-DD"),
        finPaymentmethodId: paymentObj.finPaymentmethodId,
        finFinancialAccount2Id: paymentObj.finDayCloseAccountId || null,
        opening: event.openingCash,
        opentime: dayjs(event.openingTime).format("YYYY-MM-DD HH:mm:ss"),
        closetime: now(),
        sales: parseFloat((paymentObj?.cashSaleAmount ?? 0).toFixed(prc)),
        netsales: netSalesAmount > 0 ? parseFloat(netSalesAmount.toFixed(prc)) : 0,
        cashin: cio.cashIn,
        cashout: cio.cashOut,
        retainAmt,
        closing: posConfig?.retainCashforNextShift !== "Y" ? parseFloat((event.openingCash + event.closingCash)?.toFixed(prc)) : parseFloat(event.closingCash?.toFixed(prc)),
        returns: parseFloat(paymentObj.paymentReturn.toFixed(prc)),
        netReturns: parseFloat(returnTaxAmount.toFixed(prc)),
        iscash: 1,
        isclose: 1,
        notes: noteValue,
        pettyCashIn: cio.pettyCashIn,
        pettyCashOut: cio.pettyCashOut,
        cashInOut: cio.entries,
        cashEvents: [
          {
            cwrCashEventsID: generateId(),
            amount: parseFloat(event.openingCash.toFixed(prc)),
            expected: 0,
            diff: 0,
            transactionCount: 0,
            type: "Open",
            description: "",
            cashEventDetails: openedCashEvents,
          },
          {
            cwrCashEventsID: generateId(),
            amount: parseFloat(event.closingCash.toFixed(prc)),
            expected: parseFloat(paymentObj.expectedAmount.toFixed(prc)),
            diff: parseFloat((paymentObj.difference || 0).toFixed(prc)),
            transactionCount: paymentObj.transactionCount,
            type: "Close",
            description: "",
            cashToKeep: retainAmt,
            cashEventDetails: closedCashEvents,
          },
        ],
      });
    } else {
      // Non-cash payment
      const ordersDataArr = await db.ordersData.toArray();
      const matched = getMatchedTillSession(ordersDataArr, event.tillSessionId);

      // [DI-14] Guard against division by zero when grossTotal/grossReturnTotal is 0
      const netSalesAmount = paymentObj.expectedAmount > 0 && matched.grossTotal ? parseFloat(matched.netTotal * (paymentObj.expectedAmount / matched.grossTotal)) : 0;
      const returnTaxAmount =
        Math.abs(matched.totaReturnlTax) > 0 && Math.abs(paymentObj.paymentReturn) > 0 && Math.abs(matched.grossReturnTotal) > 0
          ? matched.netReturnTotal * (paymentObj.paymentReturn / matched.grossReturnTotal)
          : 0;

      tillCash.push({
        cwrTillCashId: generateId(),
        // BUG-2171: see the cash entry above.
        date: getTillBusinessDate(event) || dayjs().format("YYYY-MM-DD"),
        finPaymentmethodId: paymentObj.finPaymentmethodId,
        finFinancialAccount2Id: paymentObj.finDayCloseAccountId || null,
        opening: 0,
        opentime: dayjs(event.openingTime).format("YYYY-MM-DD HH:mm:ss"),
        closetime: now(),
        sales: paymentObj.name.toLowerCase() !== "cafe tips" ? parseFloat(paymentObj.actualAmount.toFixed(prc)) : 0,
        netsales: netSalesAmount > 0 ? parseFloat(netSalesAmount.toFixed(prc)) : 0,
        cashin: 0,
        cashout: 0,
        retainAmt: 0,
        notes: noteValue,
        closing: paymentObj.difference === 0 ? parseFloat(parseFloat(paymentObj.amount).toFixed(2)) : parseFloat(parseFloat(paymentObj.actualAmount).toFixed(2)),
        returns: parseFloat(paymentObj.paymentReturn.toFixed(prc)),
        netReturns: parseFloat(returnTaxAmount.toFixed(prc)),
        iscash: 0,
        isclose: 1,
        pettyCashIn: 0,
        pettyCashOut: 0,
        cashInOut: [],
        cashEvents: [
          {
            cwrCashEventsID: generateId(),
            amount: paymentObj.difference === 0 ? parseFloat(paymentObj.amount.toFixed(prc)) : parseFloat(paymentObj.actualAmount.toFixed(prc)),
            expected: parseFloat(paymentObj.expectedAmount).toFixed(prc),
            diff: parseFloat(paymentObj.difference.toFixed(prc)),
            transactionCount: paymentObj.transactionCount,
            type: "Close",
            description: "",
            cashEventDetails: [],
          },
        ],
      });
    }
  }

  return { tillCash, tillRegistration };
}

// ── Build full request string ──────────────────────────────────────────────────

async function buildSyncRequest(event, tillCash, { tillData, tillSession, closeStoreFlag }) {
  await db.cashInCashOut.clear();

  const prc = tillData.tillAccess.csBunit.currencies[0].prcPrecision;
  const taxInfo = [];
  const ordersDataArr = await db.ordersData.toArray();
  ordersDataArr.forEach((item) => {
    if (item.tillSessionId === event.tillSessionId) {
      (item.salesTax || []).forEach((tax) => {
        taxInfo.push({
          csTaxId: tax.csTaxID,
          amount: parseFloat((tax.taxAmt || 0).toFixed(prc)),
        });
      });
    }
  });

  let storeOpsTillID;
  const tillEvent = await db.tillEvents.where("tillSessionId").equals(tillSession.tillSessionId).first();
  if (tillEvent) {
    storeOpsTillID = tillEvent.storeDailyOpsTillid;
  }

  const taxInfoData = {
    cwrTillID: tillData.tillAccess.cwrTill.cwrTillID,
    csbunitID: tillData.tillAccess.csBunit.csBunitId,
    csclientID: tillData.tillAccess.csClientId,
    created: event.openingTime,
    createdby: tillData.tillAccess.csUserId,
    updated: event.openingTime,
    updatedby: tillData.tillAccess.csUserId,
    openTill: "N",
    closeStore: closeStoreFlag,
    storeDailyOpsTillid: storeOpsTillID,
    taxInfo,
    tillCash,
  };

  const stringifiedData = JSON.stringify(taxInfoData);
  const newStringifiedFields = stringifiedData.replace(/\\"/g, '\\"');
  return JSON.stringify(newStringifiedFields);
}

// ── Close a day this browser has no record of (BUG-2582) ───────────────────────
//
// Open Till was answered "Close Till is pending for this Till": the server still has this
// till's day open, but this browser has no open till event (its data was cleared), so its
// own Close Till can never run. This closes that day with what the cashier counted.
//
// Sent like a Close Till without storeDailyOpsTillid (the id is not readable from any API):
// the backend finds the day by till + store + date (the same date the refused Open Till
// sent) and closes its latest row. On that path the backend also writes the row's open time
// from opentime; null there would leave the row looking "never opened" and the next Open
// Till would reopen it, so the close time is sent (the real open time is not readable).
// Only the cash line is sent: its opening and sales are not known here (0); the other
// payment methods' rows from the open are left as they are.
export function buildPendingCloseRequest({ tillData, date, countedCash, userId, closeTime = now() }) {
  const access = tillData?.tillAccess || {};
  const methods = access.csBunit?.paymentMethodList || [];
  const cash = methods.find((p) => String(p?.name || "").toLowerCase() === "cash") || methods[0];
  if (!cash?.finPaymentmethodId) return null;
  const prc = access.csBunit?.currencies?.[0]?.prcPrecision ?? 2;
  const counted = parseFloat(Number(countedCash || 0).toFixed(prc));
  const notes = `Recovered close (BUG-2582): this browser had no record of the open day. Open time not known (set to the close time); opening and sales not known (0).`;
  const taxInfoData = {
    cwrTillID: access.cwrTill?.cwrTillID || "",
    csbunitID: access.csBunit?.csBunitId || "",
    csclientID: access.csClientId || "",
    created: closeTime,
    createdby: userId,
    updated: closeTime,
    updatedby: userId,
    openTill: "N",
    closeStore: false,
    storeDailyOpsTillid: null,
    taxInfo: [],
    tillCash: [
      {
        cwrTillCashId: generateId(),
        date,
        finPaymentmethodId: cash.finPaymentmethodId,
        finFinancialAccount2Id: cash.finDayCloseAccountId || null,
        opening: 0,
        opentime: closeTime,
        closetime: closeTime,
        sales: 0,
        netsales: 0,
        cashin: 0,
        cashout: 0,
        retainAmt: 0,
        closing: counted,
        returns: 0,
        netReturns: 0,
        iscash: 1,
        isclose: 1,
        notes,
        pettyCashIn: 0,
        pettyCashOut: 0,
        cashInOut: [],
        cashEvents: [
          {
            cwrCashEventsID: generateId(),
            amount: counted,
            expected: 0,
            diff: 0,
            transactionCount: 0,
            type: "Close",
            description: "Recovered close",
            cashToKeep: 0,
            cashEventDetails: [],
          },
        ],
      },
    ],
  };
  const stringifiedData = JSON.stringify(taxInfoData);
  const newStringifiedFields = stringifiedData.replace(/\\"/g, '\\"');
  return JSON.stringify(newStringifiedFields);
}

// Sends the close built above. { ok, message }. Never throws.
export async function closePendingTillSession({ tillData, date, countedCash, userId }) {
  const till = tillData?.tillAccess?.cwrTill?.till;
  const request = buildPendingCloseRequest({ tillData, date, countedCash, userId });
  if (!request) return { ok: false, message: "No payment method is set up for this till." };
  logTillLifecycle("warn", "Open Till: closing a day this browser has no record of", { till, date, countedCash });
  try {
    const res = await upsertTill(request);
    const result = res?.data?.data?.upsertTill;
    if (result?.status === "200") {
      logTillLifecycle("info", "Open Till: previous day closed from this browser", { till, date });
      return { ok: true };
    }
    logTillLifecycle("error", "Open Till: previous day close not accepted by the server", { till, date, message: result?.message ?? null });
    return { ok: false, message: result?.message || "The previous day could not be closed. Please try again." };
  } catch (error) {
    logTillLifecycle("error", "Open Till: previous day close failed", { till, date, error: error?.message });
    return { ok: false, message: "The previous day could not be closed. Check the connection and try again." };
  }
}

// ── Mark event status in DB ────────────────────────────────────────────────────

async function markEventClosed(event, requestString, tillRegistration, logoutRelease) {
  await db.ordersData
    .where("tillSessionId")
    .equals(event.tillSessionId)
    .modify((e) => {
      e.tillStatus = "closed";
    });
  await db.paymentsData
    .where("tillSessionId")
    .equals(event.tillSessionId)
    .modify((e) => {
      e.tillStatus = "closed";
    });
  await db.tillEvents
    .where("tillSessionId")
    .equals(event.tillSessionId)
    .modify((e) => {
      e.tillStatus = "closed";
      e.isSynced = 1;
      e.tillRegistration = tillRegistration;
      // BUG-2566: the LO rows chosen for this close; syncOfflineDta's retry sends these.
      e.logoutRelease = logoutRelease;
      e.tillInfo = requestString;
      e.user = useAuthStore.getState().userData?.user;
    });
}

async function markEventUnsynced(event) {
  await db.tillEvents
    .where("tillSessionId")
    .equals(event.tillSessionId)
    .modify((e) => {
      e.isSynced = 0;
    });
}

// ── Print receipt ──────────────────────────────────────────────────────────────

async function printCloseReceipt(closingSession, { ObFlag, CWHardwareController, cashValues, paymentsList, cashToKeep, aggregatorSalesData, posConfig }) {
  const keepAmt = cashToKeep && cashToKeep !== "" ? cashToKeep : 0;
  if (ObFlag) {
    await TillCloseHCPrint(closingSession, cashValues, paymentsList, keepAmt);
  } else if (CWHardwareController) {
    await TillCloseCWHCPrint(closingSession, cashValues, paymentsList, keepAmt, aggregatorSalesData, posConfig);
  }
  // else {
  //   tillClosingPrint(closingSession);
  // }
}

// ── Logout ─────────────────────────────────────────────────────────────────────

async function doLogout(domainURL, closeStoreFlag, posConfig, onDayEndOpen) {
  trace("TillClose", "doLogout — clearing cookies + stores");
  // Clear persisted auth data
  useSessionStore.getState().setTillSession(null);
  useSessionStore.getState().setNextBusinessDate(null);
  useAuthStore.getState().logout();

  const dayEndUrl = env.dayEndApplicationUrl;
  // The day-end app loads in an iframe on this same page and needs the session
  // cookie to authenticate its own requests — cookies are preserved only in that
  // one case. Every other exit is a real logout and must clear them.
  const openingDayEndApp = posConfig?.advanceDayClose === "Y" && closeStoreFlag && !!dayEndUrl;

  // electronAPI.auth.logout() notifies the CW Browser shell (which owns the
  // partition's persistent cookie store) but does not reliably clear THIS
  // document's own document.cookie — that must be done explicitly here in both
  // Electron and web mode, or leftover accessToken cookies let the SSO bootstrap
  // on /sign-in (Authentication/index.js `check()`) silently log the user back in.
  if (!openingDayEndApp) {
    clearAllCookies(domainURL);
  }

  // CW Browser mode: notify authBus (shell handles redirect)
  if (window.electronAPI?.auth) {
    try {
      await window.electronAPI.auth.logout();
      trace("TillClose", "authBus logout completed");
    } catch (err) {
      trace("TillClose", "authBus logout failed", { error: err?.message });
    }

    // Day-end application — open via callback (iframe modal in CW Browser)
    if (openingDayEndApp) {
      trace("TillClose", "opening day-end application (CW Browser mode)");
      if (typeof onDayEndOpen === "function") {
        onDayEndOpen(dayEndUrl);
      } else {
        // Fallback: open in new window if no callback provided
        window.open(dayEndUrl, "_blank");
      }
      return;
    }

    // Shell handles sign-in redirect; also navigate client-side as fallback
    // in case the shell redirect does not fire (e.g. previous-day till close).
    window.location.assign("/sign-in");
    return;
  }

  // Web mode (or authBus failed) — day-end application redirect
  if (openingDayEndApp) {
    trace("TillClose", "redirecting to day-end application URL (web mode)");
    if (typeof onDayEndOpen === "function") {
      onDayEndOpen(dayEndUrl);
    } else {
      window.location.href = dayEndUrl;
    }
    return;
  }

  trace("TillClose", "web mode logout — redirecting to sign-in");
  window.location.assign("/sign-in");
}

// ── DI-20: Three-way till session validation ──────────────────────────────────

/**
 * Validates till session data consistency before close.
 * Compares:
 *   1. Sum of individual order totals (orders table) vs ordersData summary grossTotal
 *   2. Sum of payment amounts from allPaymentsData vs ordersData grossTotal
 *   3. Till event opening + transaction amounts balance check
 *
 * Logs discrepancies as warnings but NEVER blocks till close — operators must
 * always be able to close their till even if there are data inconsistencies.
 *
 * @param {string} tillSessionId
 * @returns {{ valid: boolean, violations: Array<Object> }}
 */
export async function validateTillSession(tillSessionId) {
  const violations = [];

  try {
    // 1. Get all orders for this session — sum their individual totals
    const orders = await db.orders.where("tillSessionId").equals(tillSessionId).toArray();
    const orderTotalSum = orders.reduce((sum, o) => sum + (Number(o.total) || 0), 0);

    // 2. Get ordersData summary for this session
    const ordersDataArr = await db.ordersData.toArray();
    const sessionOrdersData = ordersDataArr.filter((d) => d.tillSessionId === tillSessionId);
    const summaryGrossTotal = sessionOrdersData.reduce((sum, d) => sum + (Number(d.grossTotal) || 0), 0);
    const summaryNetTotal = sessionOrdersData.reduce((sum, d) => sum + (Number(d.netTotal) || 0), 0);
    const summaryTotalTax = sessionOrdersData.reduce((sum, d) => sum + (Number(d.totalTax) || 0), 0);

    // Check 1: Individual order totals vs ordersData grossTotal
    if (orders.length > 0 && Math.abs(orderTotalSum - summaryGrossTotal) > 0.01) {
      violations.push({
        check: "orders-vs-ordersData",
        orderTotalSum: Number(orderTotalSum.toFixed(2)),
        summaryGrossTotal: Number(summaryGrossTotal.toFixed(2)),
        diff: Number((orderTotalSum - summaryGrossTotal).toFixed(2)),
        orderCount: orders.length,
      });
    }

    // Check 2: ordersData netTotal + totalTax should equal grossTotal
    const derivedGrossTotal = summaryNetTotal + summaryTotalTax;
    if (sessionOrdersData.length > 0 && Math.abs(derivedGrossTotal - summaryGrossTotal) > 0.01) {
      violations.push({
        check: "ordersData-net-plus-tax-vs-gross",
        netTotal: Number(summaryNetTotal.toFixed(2)),
        totalTax: Number(summaryTotalTax.toFixed(2)),
        derivedGross: Number(derivedGrossTotal.toFixed(2)),
        actualGross: Number(summaryGrossTotal.toFixed(2)),
        diff: Number((derivedGrossTotal - summaryGrossTotal).toFixed(2)),
      });
    }

    // 3. Get till event for this session — check payment amounts vs ordersData
    const tillEvents = await db.tillEvents.where("tillSessionId").equals(tillSessionId).toArray();
    if (tillEvents.length > 0) {
      const tillEvent = tillEvents[0];
      const allPaymentsData = tillEvent.allPaymentsData || [];

      // Sum all expected payment amounts (sales - returns)
      const totalExpectedPayments = allPaymentsData.reduce((sum, p) => sum + (Number(p.expectedAmount) || 0), 0);
      const totalPaymentReturns = allPaymentsData.reduce((sum, p) => sum + (Number(p.paymentReturn) || 0), 0);
      const netPayments = totalExpectedPayments - totalPaymentReturns;

      // Check 3: Payment expected amounts should approximate ordersData grossTotal
      // (there can be legitimate differences due to rounding, cash in/out, etc.)
      if (allPaymentsData.length > 0 && summaryGrossTotal > 0 && Math.abs(netPayments - summaryGrossTotal) > 1.0) {
        violations.push({
          check: "payments-vs-ordersData",
          totalExpectedPayments: Number(totalExpectedPayments.toFixed(2)),
          totalPaymentReturns: Number(totalPaymentReturns.toFixed(2)),
          netPayments: Number(netPayments.toFixed(2)),
          summaryGrossTotal: Number(summaryGrossTotal.toFixed(2)),
          diff: Number((netPayments - summaryGrossTotal).toFixed(2)),
        });
      }
    }

    // Log all violations as warnings — NEVER block till close
    violations.forEach((v) => {
      console.warn("[DI-20] Till close validation discrepancy:", JSON.stringify(v));
    });

    if (violations.length === 0) {
    }
  } catch (err) {
    // Validation errors must NEVER block till close
    console.error("[DI-20] Till session validation failed (non-blocking):", err?.message || err);
  }

  return { valid: violations.length === 0, violations };
}

// ── BUG-2566: release the till on the server (LO) ──────────────────────────────

// Restaurant (layout 2) and production tills may be registered on several devices,
// so a row without a device id cannot be assumed to be this device's.
function isMultiDeviceTill(tillData) {
  if (usePOSConfigStore.getState().posType === "PRD") return true;
  return String(parseTillAccess(tillData)?.layout ?? "1") === "2";
}

// The closing till's active rows on the server. Only called after upsertTill
// answered 200, and at most once per Close Till (one getTills request).
function resolveServerLogoutRelease(tillData, closingTill, cache) {
  if (!cache.server) {
    cache.server = (async () => {
      const userId = useAuthStore.getState().userData?.user_id;
      if (!userId || !closingTill?.cwrTillID) return { releases: [], source: "server", reason: "no_user_or_till" };
      const res = await getTills(userId);
      return resolveServerRelease({
        tills: res?.data?.data?.getTills,
        closingTill,
        deviceId: readOwnDeviceId(),
        multiDevice: isMultiDeviceTill(tillData),
      });
    })();
  }
  return cache.server;
}

async function sendLogoutRelease(releases) {
  const failed = [];
  for (const release of releases) {
    const res = await upsertPOSActivity(toActivityRow(release), "LO");
    if (!isActivityOk(res)) {
      failed.push({
        tillRegistrationId: release.tillRegistrationId,
        status: res?.data?.data?.upsertPOSActivity?.status ?? null,
        message: res?.data?.data?.upsertPOSActivity?.message ?? (res ? null : "no response"),
      });
    }
  }
  return { sent: releases.length, failed };
}

// ── Main: executeTillSync ──────────────────────────────────────────────────────

/**
 * Full close-till flow:
 *   1. Print receipt IMMEDIATELY (don't block on API)
 *   1b. Validate till session data consistency (warn only, never block)
 *   2. Build payload + call server API for each unsynced event
 *   3. Logout (always, regardless of API result)
 *
 * If API fails, event stays isSynced=0 for retry on next login (via syncOfflineDta).
 */
export async function executeTillSync({
  closingSession,
  tillData,
  tillSession,
  closeStoreFlag,
  noteValue,
  cashToKeep,
  checkedValue,
  cashValues,
  paymentsList,
  shouldUnlink,
  ObFlag,
  CWHardwareController,
  posConfig,
  domainURL,
  unlinkTillFn,
  aggregatorSalesData,
  onDayEndOpen,
  clearCookiesAndLocalStorageFn,
  onSyncFailure,
}) {
  // Step 1: Print receipt IMMEDIATELY — don't wait for server
  try {
    await printCloseReceipt(closingSession, {
      ObFlag,
      CWHardwareController,
      cashValues,
      paymentsList,
      cashToKeep,
      aggregatorSalesData,
      posConfig,
    });
  } catch (printErr) {
    console.error("[TillClose] Receipt print failed:", printErr);
  }

  // Step 1b: Validate till session data consistency (warn only, never block)
  try {
    const validation = await validateTillSession(tillSession.tillSessionId);
    if (!validation.valid) {
      console.warn("[TillClose] Session has", validation.violations.length, "data discrepancies — proceeding with close anyway");
    }
  } catch (valErr) {
    console.error("[TillClose] Validation error (non-blocking):", valErr?.message || valErr);
  }

  // Step 2: Get unsynced events and sync to server
  const unsyncedEvents = await getUnsyncedEvents();

  let didLogout = false;
  // BUG-2334: distinguishes "nothing was unsynced" from "sync was attempted and
  // failed" — Step 3's fallback used to run the same unconditional logout/Day-End-
  // App-open in both cases, so a real upsertTill failure (e.g. "cannot close the
  // store — open tills") still handed the cashier the Day-End reconciliation
  // screen for a store that never actually closed, with the failure silently
  // dropped to console.error only.
  let syncFailed = false;
  let lastFailureMessage = "";
  const closingTill = closingTillOf(tillData);
  const releaseCache = {};

  if (unsyncedEvents.length > 0) {
    for (const event of unsyncedEvents) {
      try {

        const { tillCash, tillRegistration } = await buildTillCashForEvent(event, {
          tillData,
          noteValue,
          cashToKeep,
          checkedValue,
          posConfig,
        });

        // BUG-2566: the LO row of the till being closed, from this device's rows.
        const localRelease = resolveLocalRelease(tillRegistration, closingTill);
        trace("TillClose", "LO release (local)", {
          source: localRelease.source,
          ids: localRelease.releases.map((r) => r.tillRegistrationId),
          closingTill: closingTill?.till,
          rowCount: tillRegistration.length,
        });
        if (tillRegistration.length === 0) {
          // The table should never be empty while a till is signed in (TIC-12088: why it
          // empties is not proven). Logged so the next occurrence shows when it happened.
          logTillLifecycle("warn", "Close Till: no till registration row on this device", {
            till: closingTill?.till,
            cwrTillID: closingTill?.cwrTillID,
            tillSessionId: event?.tillSessionId,
            hasAuthTillData: !!tillData,
          });
        }

        const requestString = await buildSyncRequest(event, tillCash, {
          tillData,
          tillSession,
          closeStoreFlag,
        });

        // Mark closed + store tillInfo in DB
        await markEventClosed(event, requestString, tillRegistration, localRelease.releases);

        // Call server API
        const tillRes = await upsertTill(requestString);
        const result = tillRes?.data?.data?.upsertTill;
        const status = result?.status;
        if (status === "200") {
          // Sync POS activity (non-blocking)
          try {
            // Two places never bill on one till: bills saved here that are not on the server
            // keep the till linked to this device (no LO). The close itself is done.
            if (releaseCache.keptFor === undefined) {
              releaseCache.keptFor = await unsentBillsKeepTill({
                db,
                tillData,
                posType: usePOSConfigStore.getState().posType,
                posConfig: usePOSConfigStore.getState().posConfig,
              }).catch((err) => {
                trace("TillClose", "unsent bill count failed, releasing as usual", { error: err?.message });
                return 0;
              });
            }
            // "LO" — releases the backend's till lock/status on close (default is "LI", for login).
            // BUG-2566: no local row of this till -> the till's active rows from the server.
            let release = localRelease;
            if (releaseCache.keptFor > 0) {
              logTillLifecycle("warn", "Close Till: till kept linked to this device, bills not on the server yet", {
                till: closingTill?.till,
                cwrTillID: closingTill?.cwrTillID,
                unsent: releaseCache.keptFor,
              });
              release = { releases: [], source: "kept", kept: true };
              await db.tillEvents
                .where("tillSessionId")
                .equals(event.tillSessionId)
                .modify((e) => {
                  e.logoutRelease = [];
                });
            } else if (release.releases.length === 0) {
              release = await resolveServerLogoutRelease(tillData, closingTill, releaseCache);
              trace("TillClose", "LO release (server)", { ...release, releases: release.releases.map((r) => r.tillRegistrationId) });
              if (release.releases.length > 0) {
                await db.tillEvents
                  .where("tillSessionId")
                  .equals(event.tillSessionId)
                  .modify((e) => {
                    e.logoutRelease = release.releases;
                  });
              }
            }
            let released = false;
            if (release.kept) {
              // Not released on purpose: the close goes on exactly as after an LO.
              released = true;
            } else if (release.releases.length === 0) {
              logTillLifecycle("error", "Close Till: till not released (no registration row to send LO for)", {
                till: closingTill?.till,
                cwrTillID: closingTill?.cwrTillID,
                reason: release.reason || release.source,
                foreignRows: release.foreign ?? null,
              });
            } else {
              const result = await sendLogoutRelease(release.releases);
              trace("TillClose", "LO sent", { source: release.source, ...result });
              if (result.failed.length > 0) {
                logTillLifecycle("error", "Close Till: LO not accepted by the server", { till: closingTill?.till, source: release.source, ...result });
              }
              released = result.failed.length === 0;
            }
            if (released) {
              // Logout must run unconditionally. posConfig.closingRegister never actually
              // gated this in the original (pre-refactor) flow — it sat beside an unconditional
              // duplicate that always ran the same logout, so closingRegister=Y just double-fired
              // harmlessly. Gating on it here (instead of removing the dead duplicate) meant any
              // tenant with closingRegister !== "Y" (the documented default) never logs out at
              // all, leaving the close-till screen stuck on "Closing...".
              try {
                // Use the mount-time shouldUnlink snapshot (not a live store read) so
                // this agrees with the Step 3 fallback below. A live read of
                // useSessionStore.getState().unlink here previously caused normal
                // shift-closes to be misidentified as unlinks whenever the flag was
                // never reset after a prior genuine unlink (see cashToKeep.js).
                didLogout = true;
                if (shouldUnlink && release.kept) {
                  // Unlink would clear this device's data with the bills still in it.
                  logTillLifecycle("warn", "Close Till: unlink not done, bills not on the server yet", { till: closingTill?.till, unsent: releaseCache.keptFor });
                  clearCookiesAndLocalStorageFn(closeStoreFlag);
                } else if (shouldUnlink) {
                  unlinkTillFn(closeStoreFlag);
                } else {
                  clearCookiesAndLocalStorageFn(closeStoreFlag);
                }
              } catch (error) {
                didLogout = true;
                clearAllCookies(domainURL);
                window.location.assign("/sign-in");
              }
            }
          } catch (actErr) {
            // BUG-2444: an LO failure (e.g. empty db.tillRegistrations → upsertPOSActivity
            // returns undefined) leaves didLogout false and falls through to Step 3.
            trace("TillClose", "LO activity failed — falling back to Step 3 exit", {
              error: actErr?.message,
              tillRegistrationCount: Array.isArray(tillRegistration) ? tillRegistration.length : null,
            });
            logTillLifecycle("error", "Close Till: LO failed", { till: closingTill?.till, error: actErr?.message });
          }
        } else {
          console.error("[TillClose] API returned non-200:", status, result?.message);
          syncFailed = true;
          lastFailureMessage = result?.message || "";
          await markEventUnsynced(event);
        }
      } catch (error) {
        console.error("[TillClose] Sync failed for session:", event.tillSessionId, error.message || error);
        syncFailed = true;
        lastFailureMessage = error?.message || "";
        try {
          await markEventUnsynced(event);
        } catch (markErr) {
          console.error("[TillClose] Failed to mark event unsynced:", markErr);
        }
      }
    }
  }

  // Step 3: Fallback — fires only when the loop never triggered logout.
  // Covers (a) 0 unsynced events (nothing to sync, not a failure — proceed as
  // usual) and (b) every event's upsertTill call actually failed (a real
  // failure — do NOT open the Day-End-App / log out as if it succeeded).
  if (!didLogout) {
    if (syncFailed) {
      // BUG-2582: the server did not accept the close, so the till's day is still open there.
      // An unlink here would clear this device's till data and the next login could only reach
      // Open Till, which the server refuses with "Close Till is pending for this Till".
      if (shouldUnlink) {
        logTillLifecycle("warn", "Close Till: unlink not done, close not accepted by the server", {
          message: lastFailureMessage,
        });
      }
      trace("TillClose", "sync failed — not opening day-end app / not logging out", { lastFailureMessage });
      if (typeof onSyncFailure === "function") {
        onSyncFailure(lastFailureMessage);
      }
    } else if (shouldUnlink) {
      unlinkTillFn(closeStoreFlag);
    } else if (typeof clearCookiesAndLocalStorageFn === "function") {
      // BUG-2444: same exit as a successful close (above). doLogout() calls
      // useAuthStore.logout() before opening the Day-End-App, which clears the
      // session and lets PrivateRoute bounce /close-till to /sign-in — so a close
      // whose LO call failed lost the Day-End popup.
      clearCookiesAndLocalStorageFn(closeStoreFlag);
    } else {
      doLogout(domainURL, closeStoreFlag, posConfig, onDayEndOpen);
    }
  }
}
